import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useCustomerDetails } from "hooks/useCustomerDetails";
import { useUpdateClientPersonalInfo } from "hooks/api/client/useUpdateClientPersonalInfo";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { resolveCountryCode } from "@constants";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { ClientInfo } from "./interface";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import { useSelector } from "react-redux";
import * as selectors from "selectors";

export const PersonalInformation: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions = useSelector(selectors.getUserPermissionsList);
  const canEdit = !isCaregiver && Boolean(userPermissions?.edit_client_info);

  const [editMode, setEditMode] = useState(false);
  const [ethnicityList, setEthnicityList] = useState<string[]>([]);

  const {
    data: customerDetails,
    isLoading: clientsLoading,
    refetch,
  } = useCustomerDetails(clientId);
  const { updateClientPersonalInfo, isUpdating } = useUpdateClientPersonalInfo({
    onSuccess: async () => {
      await refetch();
      setEditMode(false);
    },
  });

  const clientInfo: ClientInfo | null = customerDetails || null;

  const [formValues, setFormValues] = useState({
    firstName: "",
    preferredFirstName: "",
    lastName: "",
    dob: null as Date | null,
    gender: "",
    identifiedGender: "",
    transgender: false,
    pronouns: "",
    livesWith: "",
    address1: "",
    address2: "",
    state: "",
    city: "",
    zipCode: "",
    clientTimezone: "",
    country: "US",
    ethnicity: "",
    primaryLanguage: "",
    needsInterpreter: false,
    preAuthRequired: false,
    emergencyContactName: "",
    emergencyContactPhoneNumber: "",
    emergencyContactRelationshipToClient: "",
    requiresTablet: false,
  });

  const [initialFormValues, setInitialFormValues] = useState<any>(null);

  useEffect(() => {
    if (clientInfo) {
      const initialValues = {
        firstName: clientInfo.name || "",
        preferredFirstName: clientInfo.preferred_first_name || "",
        lastName: clientInfo.child_last_name || "",
        dob: clientInfo.dob ? dateWithoutTimezone(clientInfo.dob) : null,
        gender: clientInfo.gender || "",
        identifiedGender: clientInfo.identified_gender || "",
        transgender: clientInfo.transgender || false,
        pronouns: clientInfo.pronouns || "",
        livesWith: clientInfo.lives_with || "",
        address1: clientInfo.line1 || "",
        address2: clientInfo.line2 || "",
        state: clientInfo.state || "",
        city: clientInfo.city || "",
        zipCode: clientInfo.zip || "",
        clientTimezone: clientInfo.timezone || "",
        country: resolveCountryCode(clientInfo.country),
        ethnicity: clientInfo.ethnicity || "",
        primaryLanguage: clientInfo.primary_language || "",
        needsInterpreter: clientInfo.needs_interpreter || false,
        preAuthRequired: clientInfo.pre_auth_required || false,
        emergencyContactName: clientInfo.emergency_contact_name || "",
        emergencyContactPhoneNumber: clientInfo.emergency_contact_phone_number || "",
        emergencyContactRelationshipToClient: clientInfo.emergency_contact_relationship_to_client || "",
        requiresTablet: clientInfo.requires_tablet || false,
      };
      setFormValues(initialValues);
      setInitialFormValues(initialValues);

      if (clientInfo.ethnicity) {
        setEthnicityList(clientInfo.ethnicity.split(",").map((item: string) => item.trim()));
      }
    }
  }, [clientInfo]);

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues({ ...formValues, [field]: event.target.value });
    };

  const handleChangeYesNo = (field: string) => (event: any) => {
    const value = event.target.value === "Yes";
    setFormValues({ ...formValues, [field]: value });
  };

  const handleEthnicityChange = (event: any) => {
    const { value } = event.target;
    const newList =
      typeof value === "string" ? value.split(",").map((item: string) => item.trim()) : value;
    setEthnicityList(newList);
    setFormValues({
      ...formValues,
      ethnicity: typeof value === "string" ? value : value.join(", "),
    });
  };

  const handleDateChange = (field: string) => (value: Date | null) => {
    setFormValues({ ...formValues, [field]: value });
  };

  const handleSave = () => {
    if (!clientId) return;

    const payload = {
      clientId,
      data: {
        firstName: formValues.firstName,
        preferredFirstName: formValues.preferredFirstName,
        lastName: formValues.lastName,
        dob: formValues.dob,
        gender: formValues.gender,
        identifiedGender: formValues.identifiedGender,
        transgender: formValues.transgender,
        pronouns: formValues.pronouns,
        livesWith: formValues.livesWith,
        ethnicity: formValues.ethnicity,
        primaryLanguage: formValues.primaryLanguage,
        needsInterpreter: formValues.needsInterpreter,
        preAuthRequired: formValues.preAuthRequired,
        emergencyContactName: formValues.emergencyContactName,
        emergencyContactPhoneNumber: formValues.emergencyContactPhoneNumber,
        emergencyContactRelationshipToClient: formValues.emergencyContactRelationshipToClient,
        requiresTablet: formValues.requiresTablet,
        location: {
          line1: formValues.address1,
          line2: formValues.address2,
          state: formValues.state,
          city: formValues.city,
          zip: formValues.zipCode,
          country: formValues.country,
        },
        clientTimezone: formValues.clientTimezone,
      },
    };

    updateClientPersonalInfo(payload);
  };

  const handleCancel = () => {
    if (clientInfo) {
      setFormValues({
        firstName: clientInfo.name || "",
        preferredFirstName: clientInfo.preferred_first_name || "",
        lastName: clientInfo.child_last_name || "",
        dob: clientInfo.dob ? dateWithoutTimezone(clientInfo.dob) : null,
        gender: clientInfo.gender || "",
        identifiedGender: clientInfo.identified_gender || "",
        transgender: clientInfo.transgender || false,
        pronouns: clientInfo.pronouns || "",
        livesWith: clientInfo.lives_with || "",
        address1: clientInfo.line1 || "",
        address2: clientInfo.line2 || "",
        state: clientInfo.state || "",
        city: clientInfo.city || "",
        zipCode: clientInfo.zip || "",
        clientTimezone: clientInfo.timezone || "",
        country: resolveCountryCode(clientInfo.country),
        ethnicity: clientInfo.ethnicity || "",
        primaryLanguage: clientInfo.primary_language || "",
        needsInterpreter: clientInfo.needs_interpreter || false,
        preAuthRequired: clientInfo.pre_auth_required || false,
        emergencyContactName: clientInfo.emergency_contact_name || "",
        emergencyContactPhoneNumber: clientInfo.emergency_contact_phone_number || "",
        emergencyContactRelationshipToClient: clientInfo.emergency_contact_relationship_to_client || "",
        requiresTablet: clientInfo.requires_tablet || false,
      });
      if (clientInfo.ethnicity) {
        setEthnicityList(clientInfo.ethnicity.split(",").map((item: string) => item.trim()));
      }
    }
    setEditMode(false);
  };

  if (clientsLoading || isUpdating) {
    return <ANLoadingScreen />;
  }

  if (!clientInfo) {
    return (
      <Box sx={{ p: 5 }}>
        <Typography>No client information found</Typography>
      </Box>
    );
  }

  return (
    <SectionWrapper
      title="Personal Information"
      description="Manage client details such as name, address, and demographics"
      editMode={editMode}
      onEdit={canEdit ? () => setEditMode(true) : undefined}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={JSON.stringify(formValues) === JSON.stringify(initialFormValues)}
    >
      {editMode ? (
        <EditFields
          formValues={formValues}
          ethnicityList={ethnicityList}
          handleChange={handleChange}
          handleChangeYesNo={handleChangeYesNo}
          handleEthnicityChange={handleEthnicityChange}
          handleDateChange={handleDateChange}
          setFormValues={setFormValues}
        />
      ) : (
        <ViewFields clientInfo={clientInfo} />
      )}
    </SectionWrapper>
  );
};

export default PersonalInformation;
